import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../../../../../../app/Context';
import { i18nNS } from '../../../../../../i18n';
import { FormValues } from './types';
import { useTimerContext } from '../../Context';
import { getTimerMetaData, setTimerMetaData } from '../../../../../../db/timer';

const useTimerForm = () => {
  const { t } = useTranslation([i18nNS.COMMON]);

  const { presentationId, activeSlideId, setSlideTitleAndSubtitle } = useAppContext();
  const { countdownStartTime, setCountdownStartTime } = useTimerContext();

  const formMethods = useForm<FormValues>({
    defaultValues: {
      minutes1: '0',
      minutes2: '0',
      seconds1: '0',
      seconds2: '0',
    },
    mode: 'all',
  });

  const { setValue } = formMethods;

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    if (!presentationId) return;
    if (!activeSlideId) return;

    const userInputSeconds = +`${data.seconds1}${data.seconds2}`;
    const userInputMinutes = +`${data.minutes1}${data.minutes2}`;

    const seconds = userInputSeconds % 60;
    const carryMinutes = Math.floor(userInputSeconds / 60);
    const minutes = (userInputMinutes + carryMinutes) % 100;

    const finalTime = minutes * 60 + seconds;

    const displayMinutes = `${minutes}`.padStart(2, '0');
    const displaySeconds = `${seconds}`.padStart(2, '0');

    setCountdownStartTime(finalTime);

    setTimerMetaData(presentationId, activeSlideId, { countdownFrom: finalTime });

    setValue('minutes1', displayMinutes[0]);
    setValue('minutes2', displayMinutes[1]);
    setValue('seconds1', displaySeconds[0]);
    setValue('seconds2', displaySeconds[1]);

    const slideTitle = t('acadly_activity_plugin', { ns: i18nNS.COMMON });

    let slideSubtitle = t('timer_was_not_started', { ns: i18nNS.TIMER });

    if (finalTime > 0) {
      const response = getTimerMetaData(presentationId, activeSlideId);
      if (!response) return;
      const { title: timerTitle } = response;
      slideSubtitle = t('title_timer_ran_for_minutes_count_minutes_seconds_count_seconds', {
        ns: i18nNS.TIMER,
        title: timerTitle ?? t('untitled_timer', { ns: i18nNS.TIMER }),
        minutesCount: displayMinutes,
        secondsCount: displaySeconds,
      });
    }

    setSlideTitleAndSubtitle({ title: slideTitle, subtitle: slideSubtitle });
  };

  useEffect(
    function onMount() {
      if (!presentationId) return;
      if (!activeSlideId) return;

      const minutes = `${Math.floor(countdownStartTime / 60)}`.padStart(2, '0');
      const seconds = `${countdownStartTime % 60}`.padStart(2, '0');

      setValue('minutes1', minutes[0]);
      setValue('minutes2', minutes[1]);
      setValue('seconds1', seconds[0]);
      setValue('seconds2', seconds[1]);
    },
    [setValue, presentationId, activeSlideId, countdownStartTime]
  );

  return { formMethods, onSubmit };
};

export default useTimerForm;
